import {
  AnalyticsFieldName,
  ProfileAnalyticsEvent,
} from "@/constants/analytics/Analytics";
import { ModalPath, ScreenPath, TabPath } from "@/constants/Navigation";
import { ProfileTestID } from "@/constants/TestID";
import { router } from "@/crossPlatform/expo-router";
import { useAnalytics } from "@/services/Analytics";
import { Button, FAIconName, LabelWithIcon, Text } from "@/ui/core";
import { BaseCard, HStack, VStack } from "@/ui/layout";
import { ColorToken } from "@/ui/theme/tokens";
import { isWeb } from "@/utils/platform.util";
import { joinTestIDs } from "@/utils/string.util";

const chooseIcon = (isEnabled: boolean) =>
  isEnabled ? FAIconName.Check : FAIconName.Times;
const chooseColor = (isEnabled: boolean) =>
  isEnabled ? ColorToken.Primary100 : ColorToken.NeutralTertiary;

interface NotificationSettingsOverviewCardProps {
  isEmailEnabled: boolean;
  isPushEnabled: boolean;
  isSmsEnabled: boolean;
}

const NotificationSettingsOverviewCard = ({
  isEmailEnabled,
  isPushEnabled,
  isSmsEnabled,
}: NotificationSettingsOverviewCardProps) => {
  const { track } = useAnalytics();
  const baseTestID = ProfileTestID.NotificationSettingsOverviewCard;

  const handleButtonPress = () => {
    track(ProfileAnalyticsEvent.EditNotificationSettings, {
      [AnalyticsFieldName.Component]: NotificationSettingsOverviewCard.name,
      [AnalyticsFieldName.Source]: `${ScreenPath.MainTabs}/${TabPath.Profile}`,
    });
    router.navigate({
      pathname: ModalPath.EditNotificationSettings,
    });
  };

  return (
    <BaseCard testID={baseTestID}>
      <VStack gap={12}>
        <HStack alignItems="center" justifyContent="space-between">
          <Text testID={joinTestIDs(baseTestID, "header")} variant="bodyMedium">
            Notification settings
          </Text>
          <Button
            leftIcon={FAIconName.Pen}
            onPress={handleButtonPress}
            size="small"
            testID={joinTestIDs(baseTestID, "edit")}
            title="Edit"
            variant="secondary"
          />
        </HStack>
        <VStack gap={8}>
          {!isWeb() && (
            <LabelWithIcon
              iconColor={chooseColor(isPushEnabled)}
              iconName={chooseIcon(isPushEnabled)}
              testID={joinTestIDs(baseTestID, "push")}
              text="Push notifications"
            />
          )}
          <LabelWithIcon
            iconColor={chooseColor(isSmsEnabled)}
            iconName={chooseIcon(isSmsEnabled)}
            testID={joinTestIDs(baseTestID, "sms")}
            text="Text messages"
          />
          <LabelWithIcon
            iconColor={chooseColor(isEmailEnabled)}
            iconName={chooseIcon(isEmailEnabled)}
            testID={joinTestIDs(baseTestID, "email")}
            text="Email"
          />
        </VStack>
      </VStack>
    </BaseCard>
  );
};

export default NotificationSettingsOverviewCard;
export type { NotificationSettingsOverviewCardProps };
